import { useCallback, useEffect, useState } from 'react';
import { adminJson } from '../api';
import { track } from '../lib/analytics';

// Admin → Shareable: crafted X-post cards from the curated archive (port of
// the 911exchange Shareable engine). Edit the text, then post via the X API
// (when credentials exist), the X intent composer, or copy. Every link carries
// per-post utm_content attribution so first-party analytics tie clicks to the
// exact post that drove them.

// X counts every URL as 23 chars. Cards keep the URL out of the main text
// (the API path posts the link as a reply), so for intent/copy composition we
// add the link's weight when showing the counter.
const X_URL_WEIGHT = 23;
const X_MAX = 280;

const CATEGORY_STYLES = {
  nostalgia: 'bg-amber-100 text-amber-800',
  cliffhanger: 'bg-purple-100 text-purple-800',
  throwback: 'bg-sky-100 text-sky-800',
  archive: 'bg-emerald-100 text-emerald-800',
};

const CATEGORY_LABELS = {
  nostalgia: 'Nostalgia',
  cliffhanger: 'Cliffhanger',
  throwback: 'Throwback',
  archive: 'Archive',
};

// twitter-text v3 weight-1 codepoint ranges; everything else (emoji, CJK,
// most symbols) weighs 2. Mirrors backend x_weighted_len — archive captions
// carry 2012-era emoji, and a counter that undercounts them would greenlight
// a post the X API rejects as over-limit.
const X_WEIGHT1_RANGES = [
  [0x0000, 0x10ff],
  [0x2000, 0x200d],
  [0x2010, 0x201f],
  [0x2032, 0x2037],
];

function charWeight(cp) {
  return X_WEIGHT1_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi) ? 1 : 2;
}

function weightedLen(text) {
  const urls = (text.match(/https?:\/\/\S+/g) || []).length;
  const stripped = text.replace(/https?:\/\/\S+/g, '');
  let n = 0;
  for (const ch of stripped) n += charWeight(ch.codePointAt(0)); // code points, not UTF-16 units
  return n + urls * X_URL_WEIGHT;
}

function composeWithLink(text, link) {
  return link ? `${text}\n\n${link}` : text;
}

// X caches the link-card scrape PER URL (~a week): resharing a card with the
// identical URL serves the STALE card even on a brand-new tweet. Stamping the
// share date into utm_term makes each day's share a fresh URL — fresh scrape —
// and rides the existing first-party attribution as a free share-day
// dimension. Computed at SHARE time (not render), so a reshared card's logged
// link gets today's stamp, not the original share's.
function freshLink(link) {
  if (!link) return link;
  try {
    const u = new URL(link);
    const d = new Date();
    const stamp = `s${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
    u.searchParams.set('utm_term', stamp);
    return u.toString();
  } catch {
    return link;
  }
}

// The card link with tracking params stripped — for the admin REVIEWING the
// poll before sharing (a utm-tagged visit would pollute the per-post
// attribution the shared link exists to measure).
function previewUrl(link) {
  if (!link) return null;
  try {
    const u = new URL(link);
    [...u.searchParams.keys()]
      .filter((k) => k.startsWith('utm_'))
      .forEach((k) => u.searchParams.delete(k));
    return u.toString();
  } catch {
    return link;
  }
}

function ShareCard({ card, xConfigured, onPublished }) {
  const [text, setText] = useState(card.text);
  const [busy, setBusy] = useState(false);
  // Inline per-card status (votomoto has no toast layer): {kind, text}.
  const [notice, setNotice] = useState(null);

  // Two different budgets: the API path posts ONLY the text (the link goes in
  // the reply tweet), so "Post to X" gates on mainLen; intent/copy compose
  // text+link into a single tweet, so those gate on composedLen. Gating every
  // button on composedLen would wrongly block valid API posts at 256–280.
  const mainLen = weightedLen(text);
  const composedLen = weightedLen(composeWithLink(text, card.link));
  const apiOverLimit = mainLen > X_MAX;
  const composedOverLimit = composedLen > X_MAX;

  const log = useCallback(
    async (method, link) => {
      const result = await adminJson('/admin/shareable/publish', {
        method: 'POST',
        body: JSON.stringify({
          card_id: card.card_id,
          category: card.category,
          text,
          method,
          // The exact URL that was shared (freshLink-stamped by the caller)
          // — share_log must record what actually went out.
          link,
          link_text: card.link_text,
          image: card.image,
        }),
      });
      // Emit the share act into the first-party analytics stream (same
      // collector the click beacons use), keyed by card_id — this is what
      // lets Shared Content Performance join shares to the clicks they drove.
      track('content_share', {
        card_id: card.card_id,
        category: card.category,
        method,
      });
      return result;
    },
    [card, text]
  );

  const postViaApi = async () => {
    if (
      !window.confirm(
        'Post to X now?\n\nPosts the image tweet, then the link as a reply (~$0.22 in X API credits).'
      )
    )
      return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await log('api', freshLink(card.link));
      if (result.log_error) {
        // The tweet is PUBLIC but the share_log write failed — without the
        // row this card would resurface in the slate as if never shared.
        setNotice({ kind: 'warn', text: 'Posted to X, but logging failed — this card may reappear; do NOT repost it.' });
      } else if (result.reply_error) {
        setNotice({ kind: 'warn', text: 'Posted, but the link reply failed — add it manually from the tweet.' });
      } else {
        setNotice({ kind: 'ok', text: 'Posted to X.' });
      }
      if (result.tweet_url) window.open(result.tweet_url, '_blank', 'noopener');
      onPublished();
    } catch (err) {
      setNotice({ kind: 'error', text: err.detail || err.message || 'Post failed' });
    } finally {
      setBusy(false);
    }
  };

  const openIntent = () => {
    // window.open must run synchronously in the click handler (popup blockers);
    // the log call rides behind it.
    const link = freshLink(card.link);
    const url = `https://x.com/intent/post?text=${encodeURIComponent(
      composeWithLink(text, link)
    )}`;
    window.open(url, '_blank', 'noopener');
    log('intent', link).then(onPublished).catch(() => {});
  };

  const copyText = async () => {
    try {
      const link = freshLink(card.link);
      await navigator.clipboard.writeText(composeWithLink(text, link));
      setNotice({ kind: 'ok', text: 'Copied.' });
      log('copy', link).then(onPublished).catch(() => {});
    } catch {
      setNotice({ kind: 'error', text: 'Clipboard unavailable' });
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-voto-line bg-voto-surface">
      {card.image && (
        <img
          src={card.image}
          alt=""
          loading="lazy"
          className="h-44 w-full bg-voto-raised object-cover"
        />
      )}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 text-xs font-semibold ${
              CATEGORY_STYLES[card.category] || 'bg-voto-raised text-voto-fg'
            }`}
          >
            {CATEGORY_LABELS[card.category] || card.category}
          </span>
          <span className="truncate text-sm font-medium text-voto-fg" title={card.title}>
            {card.title}
          </span>
          <span
            className={`ml-auto text-xs tabular-nums ${
              composedOverLimit ? 'font-semibold text-voto-coral' : 'text-voto-muted'
            }`}
            title={`Single-tweet length with the link appended (X counts every URL as 23 chars): ${composedLen}/${X_MAX}. API post (link goes in the reply): ${mainLen}/${X_MAX}.`}
          >
            {composedLen}/{X_MAX}
          </span>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={Math.min(8, Math.max(4, text.split('\n').length + 1))}
          className="w-full rounded border border-voto-line bg-voto-raised p-2 text-sm leading-snug text-voto-fg"
        />

        <div className="truncate text-xs text-voto-muted" title={card.link}>
          ↳ {card.link}
        </div>

        {notice && (
          <p
            className={`rounded p-2 text-xs ${
              notice.kind === 'error'
                ? 'bg-voto-coral/10 text-voto-coral'
                : notice.kind === 'warn'
                ? 'bg-amber-50 text-amber-800'
                : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {notice.text}
          </p>
        )}

        <div className="mt-auto flex flex-wrap gap-2">
          {xConfigured && (
            <button
              type="button"
              onClick={postViaApi}
              disabled={busy || apiOverLimit}
              className="rounded-md bg-voto-chrome px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              {busy ? 'Posting…' : 'Post to X'}
            </button>
          )}
          <button
            type="button"
            onClick={openIntent}
            disabled={composedOverLimit}
            className="rounded-md border border-voto-line px-3 py-1.5 text-sm font-medium text-voto-fg hover:bg-voto-raised disabled:opacity-40"
          >
            Open in X
          </button>
          <button
            type="button"
            onClick={copyText}
            className="rounded-md border border-voto-line px-3 py-1.5 text-sm font-medium text-voto-fg hover:bg-voto-raised"
          >
            Copy
          </button>
          {card.link && (
            <a
              href={previewUrl(card.link)}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-voto-line px-3 py-1.5 text-sm font-medium text-voto-fg hover:bg-voto-raised"
              title="Review the poll before sharing (tracking params stripped so your visit doesn't pollute the post's attribution)"
            >
              {card.link.includes('/poll/') ? 'View poll' : 'View page'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminShareable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Cards lifted back out of the share history for editing/resharing.
  const [reshareCards, setReshareCards] = useState([]);
  // Per-card click stats, fetched separately (cross-service proxy) so it never
  // sits on the tab's render path — fills the Clicks column after cards render.
  const [clickStats, setClickStats] = useState(null);

  const reshare = (r) => {
    const card = {
      card_id: r.card_id,
      category: r.category,
      title: `${CATEGORY_LABELS[r.category] || r.category} (reshare)`,
      text: r.text,
      link: r.link,
      link_text: r.link_text,
      image: r.image,
      // Namespaces the React key: if the same card_id has ALSO regenerated as
      // a fresh card, reusing its key would keep the mounted composer's state
      // (fresh text under a "(reshare)" title). A distinct key forces a
      // remount with the logged text.
      _reshare: true,
    };
    setReshareCards((prev) =>
      prev.some((c) => c.card_id === card.card_id) ? prev : [card, ...prev]
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminJson('/admin/shareable'));
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Click stats load independently of the main tab (auxiliary, fail-soft).
  useEffect(() => {
    let cancelled = false;
    adminJson('/admin/shareable/clicks')
      .then((b) => {
        if (!cancelled) setClickStats(b.cards || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading && !data) return <p className="mt-8 text-sm text-voto-muted">Loading…</p>;
  if (!data)
    return (
      <p className="mt-8 rounded-lg bg-voto-coral/10 p-3 text-sm text-voto-coral">
        {error ? 'Failed to load shareable posts.' : null}
      </p>
    );

  const reshareIds = new Set(reshareCards.map((c) => c.card_id));
  const freshCards = data.cards.filter((c) => !reshareIds.has(c.card_id));
  const allCards = [...reshareCards, ...freshCards];

  return (
    <section className="mt-8 space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-voto-fg">Shareable</h2>
        <span
          className={`rounded px-2 py-0.5 text-xs font-semibold ${
            data.x_configured
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-voto-raised text-voto-muted'
          }`}
        >
          {data.x_configured
            ? 'X API connected'
            : 'X API not configured — manual share only'}
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="ml-auto rounded-md border border-voto-line px-3 py-1.5 text-sm font-medium text-voto-fg hover:bg-voto-raised disabled:opacity-40"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <p className="max-w-3xl text-sm text-voto-muted">
        Crafted posts from the curated archive. Edit the text, then post. Links
        carry per-post attribution (utm_content = card id), so clicks land in
        the analytics with the exact post that drove them. Shared cards leave
        the list for 30 days; the slate rotates weekly.
      </p>

      {allCards.length === 0 ? (
        <p className="text-sm text-voto-muted">
          Nothing new to share — everything current was shared recently.
          Reshare from the history below.
        </p>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {allCards.map((card) => (
            <ShareCard
              key={card._reshare ? `re:${card.card_id}` : card.card_id}
              card={card}
              xConfigured={data.x_configured}
              onPublished={() => {
                setReshareCards((prev) =>
                  prev.filter((c) => c.card_id !== card.card_id)
                );
                load();
              }}
            />
          ))}
        </div>
      )}

      <section>
        <h3 className="mb-1 text-base font-semibold text-voto-fg">Share history</h3>
        <p className="mb-3 text-xs text-voto-muted">
          Each card's latest share. Reshare loads it back into the composer
          above for editing — API posting of the same card is limited to once
          per day.
        </p>
        {data.recent.length === 0 ? (
          <p className="text-sm text-voto-muted">No shares yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-voto-line bg-voto-surface">
            <table className="min-w-full divide-y divide-voto-line text-sm">
              <thead className="bg-voto-raised text-left text-xs uppercase tracking-wide text-voto-muted">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2 text-right">Clicks</th>
                  <th className="px-3 py-2">Post</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-voto-line">
                {data.recent.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-voto-muted">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-semibold ${
                          CATEGORY_STYLES[r.category] || 'bg-voto-raised text-voto-fg'
                        }`}
                      >
                        {CATEGORY_LABELS[r.category] || r.category}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-voto-fg">{r.method}</td>
                    <td
                      className="whitespace-nowrap px-3 py-2 text-right tabular-nums"
                      title="X clicks attributed to this card (visitors in parens)"
                    >
                      {(() => {
                        if (clickStats == null)
                          return <span className="text-voto-muted">·</span>;
                        const s = clickStats[r.card_id];
                        if (!s || s.click_events == null)
                          return <span className="text-voto-muted">—</span>;
                        return (
                          <span className="text-voto-fg">
                            {s.click_events}
                            <span className="text-voto-muted"> ({s.click_visitors})</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="max-w-md truncate px-3 py-2 text-voto-fg" title={r.text}>
                      {r.text}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      <button
                        type="button"
                        onClick={() => reshare(r)}
                        className="mr-2 rounded border border-voto-line px-2 py-1 text-voto-fg hover:bg-voto-raised"
                      >
                        Reshare
                      </button>
                      {r.tweet_id && (
                        <a
                          href={`https://x.com/i/web/status/${r.tweet_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-voto-coral hover:underline"
                        >
                          View on X
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
